import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Filter, ArrowUpRight, ArrowDownRight, FileText, FileCheck, Route, Layers, Building2, Hash, Container, PackageCheck, Pencil, Star } from "lucide-react";
import { OrderStatus, ORDER_STATUS_LABELS, Location, TripType, Customer } from "@/types/tms";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";
import { SearchChip } from "@/components/ui/search-chip";
import { DateRange } from "@/hooks/useOrdersQuery";
import { DateFilterPopover } from "@/components/orders/DateFilterPopover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const ORDERED_STATUSES: OrderStatus[] = ["booked", "confirmed", "in_transit", "delivered", "canceled", "tender_received"];

const STATUS_OPTIONS: ChipFilterOption[] = ORDERED_STATUSES.map((s) => ({
  value: s,
  label: ORDER_STATUS_LABELS[s],
}));

const TENDER_OPTIONS: ChipFilterOption[] = [
  { value: "has", label: "Has Load Tender" },
  { value: "missing", label: "Missing Load Tender" },
];

const BOL_OPTIONS: ChipFilterOption[] = [
  { value: "has", label: "Has BOL" },
  { value: "missing", label: "Missing BOL" },
];

const TENDER_ID_OPTIONS: ChipFilterOption[] = [
  { value: "has", label: "Has Tender ID" },
  { value: "missing", label: "Missing Tender ID" },
];

const TRAILER_LOAD_STATUS_OPTIONS: ChipFilterOption[] = [
  { value: "loaded", label: "Loaded" },
  { value: "empty", label: "Empty" },
  { value: "unknown", label: "Unknown" },
];

const API_CREATED_OPTIONS: ChipFilterOption[] = [
  { value: "api", label: "API" },
  { value: "manual", label: "Manual creation" },
];

const TRIP_TYPE_OPTIONS: ChipFilterOption[] = [
  { value: "NVO", label: "NVO" },
  { value: "SVO", label: "SVO" },
  { value: "Manual", label: "Manual" },
  { value: "CI", label: "CI" },
];

const ORDER_TYPE_OPTIONS: ChipFilterOption[] = [
  { value: "single_leg", label: "Single-leg" },
  { value: "multi_leg", label: "Multi-leg" },
];

interface OrderFiltersProps {
  statusFilter: string | string[];
  onStatusFilterChange: (value: string[]) => void;
  dateFilter: DateRange;
  onDateFilterChange: (date: DateRange) => void;
  shipFromFilter?: string[];
  onShipFromFilterChange?: (value: string[]) => void;
  shipToFilter?: string[];
  onShipToFilterChange?: (value: string[]) => void;
  locations?: Location[];
  customerFilter?: string[];
  onCustomerFilterChange?: (value: string[]) => void;
  customers?: Customer[];
  tenderFilter?: string;
  onTenderFilterChange?: (value: string) => void;
  bolFilter?: string;
  onBolFilterChange?: (value: string) => void;
  tenderIdFilter?: string;
  onTenderIdFilterChange?: (value: string) => void;
  trailerTypeFilter?: string[];
  onTrailerTypeFilterChange?: (value: string[]) => void;
  trailerLoadStatusFilter?: string[];
  onTrailerLoadStatusFilterChange?: (value: string[]) => void;
  apiCreatedFilter?: string;
  onApiCreatedFilterChange?: (value: string) => void;
  tripTypeFilter?: string | string[];
  onTripTypeFilterChange?: (value: string[]) => void;
  orderTypeFilter?: string;
  onOrderTypeFilterChange?: (value: string) => void;
  isProd?: boolean;
  searchQuery?: string;
  onClearSearch?: () => void;
  subscribedOnly?: boolean;
  onSubscribedOnlyChange?: (v: boolean) => void;
}

type FilterKey =
  | "status"
  | "ship_from"
  | "ship_to"
  | "tender"
  | "bol"
  | "tender_id"
  | "trailer_type"
  | "trailer_load_status"
  | "api_created"
  | "trip_type"
  | "order_type"
  | "customer";

export function OrderFilters({
  statusFilter,
  onStatusFilterChange,
  dateFilter,
  onDateFilterChange,
  shipFromFilter,
  onShipFromFilterChange,
  shipToFilter,
  onShipToFilterChange,
  locations,
  customerFilter,
  onCustomerFilterChange,
  customers,
  tenderFilter,
  onTenderFilterChange,
  bolFilter,
  onBolFilterChange,
  tenderIdFilter,
  onTenderIdFilterChange,
  trailerTypeFilter,
  onTrailerTypeFilterChange,
  trailerLoadStatusFilter,
  onTrailerLoadStatusFilterChange,
  apiCreatedFilter,
  onApiCreatedFilterChange,
  tripTypeFilter,
  onTripTypeFilterChange,
  orderTypeFilter,
  onOrderTypeFilterChange,
  isProd,
  searchQuery,
  onClearSearch,
  subscribedOnly,
  onSubscribedOnlyChange,
}: OrderFiltersProps) {
  const { data: trailerTypes } = useTrailerTypes();
  const [activeFilters, setActiveFilters] = useState<FilterKey[]>(() => {
    const initial: FilterKey[] = [];
    const statusArr = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
    if (statusArr.some((v) => v && v !== "all")) initial.push("status");
    if (tenderFilter && tenderFilter !== "all") initial.push("tender");
    if (bolFilter && bolFilter !== "all") initial.push("bol");
    if (tenderIdFilter && tenderIdFilter !== "all") initial.push("tender_id");
    if (trailerTypeFilter && trailerTypeFilter.some((v) => v && v !== "all")) initial.push("trailer_type");
    if (trailerLoadStatusFilter && trailerLoadStatusFilter.some((v) => v && v !== "all")) initial.push("trailer_load_status");
    if (apiCreatedFilter && apiCreatedFilter !== "all") initial.push("api_created");
    if (shipFromFilter && shipFromFilter.some((v) => v && v !== "all")) initial.push("ship_from");
    if (shipToFilter && shipToFilter.some((v) => v && v !== "all")) initial.push("ship_to");
    if (customerFilter && customerFilter.some((v) => v && v !== "all")) initial.push("customer");
    const tripArr = Array.isArray(tripTypeFilter) ? tripTypeFilter : tripTypeFilter ? [tripTypeFilter] : [];
    if (tripArr.some((v) => v && v !== "all")) initial.push("trip_type");
    if (orderTypeFilter && orderTypeFilter !== "all") initial.push("order_type");
    return initial;
  });
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<FilterKey | null>(null);

  const addFilter = useCallback((key: string) => {
    const fk = key as FilterKey;
    if (activeFilters.includes(fk)) return;

    setActiveFilters((prev) => [...prev, fk]);

    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);

      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) {
          ignoreAutoCloseRef.current = null;
        }
      }, 250);
    }, 50);
  }, [activeFilters]);

  const removeFilter = useCallback((key: FilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) {
      ignoreAutoCloseRef.current = null;
    }

    if (key === "status") onStatusFilterChange(["all"]);
    if (key === "tender") onTenderFilterChange?.("all");
    if (key === "bol") onBolFilterChange?.("all");
    if (key === "tender_id") onTenderIdFilterChange?.("all");
    if (key === "trailer_type") onTrailerTypeFilterChange?.(["all"]);
    if (key === "trailer_load_status") onTrailerLoadStatusFilterChange?.(["all"]);
    if (key === "api_created") onApiCreatedFilterChange?.("all");
    if (key === "ship_from") onShipFromFilterChange?.(["all"]);
    if (key === "ship_to") onShipToFilterChange?.(["all"]);
    if (key === "customer") onCustomerFilterChange?.(["all"]);
    if (key === "trip_type") onTripTypeFilterChange?.(["all"]);
    if (key === "order_type") onOrderTypeFilterChange?.("all");
  }, [onStatusFilterChange, onTenderFilterChange, onBolFilterChange, onTenderIdFilterChange, onTrailerTypeFilterChange, onTrailerLoadStatusFilterChange, onApiCreatedFilterChange, onShipFromFilterChange, onShipToFilterChange, onCustomerFilterChange, onTripTypeFilterChange, onOrderTypeFilterChange]);

  const hasSearch = !!searchQuery && searchQuery.trim().length > 0;
  const hasAnyChipFilter = activeFilters.length > 0 || hasSearch;

  const resetAllFilters = () => {
    setActiveFilters([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onStatusFilterChange(["all"]);
    onTenderFilterChange?.("all");
    onBolFilterChange?.("all");
    onTenderIdFilterChange?.("all");
    onTrailerTypeFilterChange?.(["all"]);
    onTrailerLoadStatusFilterChange?.(["all"]);
    onApiCreatedFilterChange?.("all");
    onShipFromFilterChange?.(["all"]);
    onShipToFilterChange?.(["all"]);
    onCustomerFilterChange?.(["all"]);
    onTripTypeFilterChange?.(["all"]);
    onOrderTypeFilterChange?.("all");
    onClearSearch?.();
  };

  const locationOptions: ChipFilterOption[] = (locations ?? []).map((loc) => {
    const code = loc.short_code?.trim();
    const city = loc.city?.trim();
    const label = code
      ? `${code}${city ? `: ${city}` : ""}`
      : city ?? "Unknown";
    return { value: loc.id, label };
  });

  const customerOptions: ChipFilterOption[] = (customers ?? [])
    .map((c) => ({ value: c.id, label: c.organization_name ?? "Unknown" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const trailerTypeOptions: ChipFilterOption[] = (trailerTypes ?? [])
    .map((t) => ({ value: t.id, label: t.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const allCategories: (FilterCategory & { key: FilterKey; available: boolean })[] = [
    { key: "status", label: "Status", icon: <Filter className="h-3.5 w-3.5" />, available: true },
    { key: "customer", label: "Customer", icon: <Building2 className="h-3.5 w-3.5" />, available: onCustomerFilterChange !== undefined },
    { key: "order_type", label: "Order Type", icon: <Layers className="h-3.5 w-3.5" />, available: onOrderTypeFilterChange !== undefined },
    { key: "trip_type", label: "Trip Type", icon: <Route className="h-3.5 w-3.5" />, available: true },
    { key: "ship_from", label: "Ship From", icon: <ArrowUpRight className="h-3.5 w-3.5" />, available: shipFromFilter !== undefined },
    { key: "ship_to", label: "Ship To", icon: <ArrowDownRight className="h-3.5 w-3.5" />, available: shipToFilter !== undefined },
    { key: "tender", label: "Load Tender", icon: <FileText className="h-3.5 w-3.5" />, available: tenderFilter !== undefined },
    { key: "tender_id", label: "Tender ID", icon: <Hash className="h-3.5 w-3.5" />, available: onTenderIdFilterChange !== undefined },
    { key: "bol", label: "Bill of Lading", icon: <FileCheck className="h-3.5 w-3.5" />, available: bolFilter !== undefined },
    { key: "trailer_type", label: "Trailer Type", icon: <Container className="h-3.5 w-3.5" />, available: onTrailerTypeFilterChange !== undefined },
    { key: "trailer_load_status", label: "Trailer Load Status", icon: <PackageCheck className="h-3.5 w-3.5" />, available: onTrailerLoadStatusFilterChange !== undefined },
    { key: "api_created", label: "Created by", icon: <Pencil className="h-3.5 w-3.5" />, available: onApiCreatedFilterChange !== undefined },
  ];

  const availableCategories = allCategories
    .filter((c) => c.available && !activeFilters.includes(c.key))
    .map(({ key, label, icon }) => ({ key, label, icon }));

  const getFilterValue = (key: FilterKey): string | string[] => {
    if (key === "status") return statusFilter;
    if (key === "tender") return tenderFilter ?? "all";
    if (key === "bol") return bolFilter ?? "all";
    if (key === "tender_id") return tenderIdFilter ?? "all";
    if (key === "trailer_type") return trailerTypeFilter ?? ["all"];
    if (key === "trailer_load_status") return trailerLoadStatusFilter ?? ["all"];
    if (key === "api_created") return apiCreatedFilter ?? "all";
    if (key === "ship_from") return shipFromFilter ?? ["all"];
    if (key === "ship_to") return shipToFilter ?? ["all"];
    if (key === "customer") return customerFilter ?? ["all"];
    if (key === "trip_type") return tripTypeFilter ?? ["all"];
    if (key === "order_type") return orderTypeFilter ?? "all";
    return "all";
  };

  const renderChip = (key: FilterKey) => {
    const categoryIcon = allCategories.find((c) => c.key === key)?.icon;
    const commonProps = {
      icon: categoryIcon,
      open: openFilterKey === key,
      onOpenChange: (isOpen: boolean) => {
        if (!isOpen && ignoreAutoCloseRef.current === key) {
          ignoreAutoCloseRef.current = null;
          return;
        }

        if (!isOpen) {
          const v = getFilterValue(key);
          const isCleared = Array.isArray(v)
            ? v.length === 0 || v.every((x) => x === "all")
            : v === "all";
          if (isCleared) {
            setActiveFilters((prev) => prev.filter((k) => k !== key));
          }
          setOpenFilterKey((prev) => (prev === key ? null : prev));
        } else {
          setOpenFilterKey(key);
        }
      },
    };

    if (key === "status") {
      return (
        <ChipFilter
          key="status"
          label="Status"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => onStatusFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("status")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "ship_from" && shipFromFilter !== undefined && onShipFromFilterChange) {
      return (
        <ChipFilter
          key="ship_from"
          label="Ship From"
          options={locationOptions}
          value={shipFromFilter}
          onChange={(v) => onShipFromFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("ship_from")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "ship_to" && shipToFilter !== undefined && onShipToFilterChange) {
      return (
        <ChipFilter
          key="ship_to"
          label="Ship To"
          options={locationOptions}
          value={shipToFilter}
          onChange={(v) => onShipToFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("ship_to")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "customer" && customerFilter !== undefined && onCustomerFilterChange) {
      return (
        <ChipFilter
          key="customer"
          label="Customer"
          options={customerOptions}
          value={customerFilter}
          onChange={(v) => onCustomerFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("customer")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "tender" && tenderFilter !== undefined && onTenderFilterChange) {
      return (
        <ChipFilter
          key="tender"
          label="Load Tender"
          options={TENDER_OPTIONS}
          value={tenderFilter}
          onChange={onTenderFilterChange}
          onRemove={() => removeFilter("tender")}
          {...commonProps}
        />
      );
    }

    if (key === "bol" && bolFilter !== undefined && onBolFilterChange) {
      return (
        <ChipFilter
          key="bol"
          label="Bill of Lading"
          options={BOL_OPTIONS}
          value={bolFilter}
          onChange={onBolFilterChange}
          onRemove={() => removeFilter("bol")}
          {...commonProps}
        />
      );
    }

    if (key === "tender_id" && tenderIdFilter !== undefined && onTenderIdFilterChange) {
      return (
        <ChipFilter
          key="tender_id"
          label="Tender ID"
          options={TENDER_ID_OPTIONS}
          value={tenderIdFilter}
          onChange={onTenderIdFilterChange}
          onRemove={() => removeFilter("tender_id")}
          {...commonProps}
        />
      );
    }

    if (key === "trailer_type" && onTrailerTypeFilterChange) {
      return (
        <ChipFilter
          key="trailer_type"
          label="Trailer Type"
          options={trailerTypeOptions}
          value={trailerTypeFilter ?? ["all"]}
          onChange={(v) => onTrailerTypeFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("trailer_type")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "trailer_load_status" && onTrailerLoadStatusFilterChange) {
      return (
        <ChipFilter
          key="trailer_load_status"
          label="Trailer Load Status"
          options={TRAILER_LOAD_STATUS_OPTIONS}
          value={trailerLoadStatusFilter ?? ["all"]}
          onChange={(v) => onTrailerLoadStatusFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("trailer_load_status")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "api_created" && apiCreatedFilter !== undefined && onApiCreatedFilterChange) {
      return (
        <ChipFilter
          key="api_created"
          label="Created by"
          options={API_CREATED_OPTIONS}
          value={apiCreatedFilter}
          onChange={onApiCreatedFilterChange}
          onRemove={() => removeFilter("api_created")}
          {...commonProps}
        />
      );
    }

    if (key === "trip_type" && onTripTypeFilterChange) {
      return (
        <ChipFilter
          key="trip_type"
          label="Trip Type"
          options={isProd ? TRIP_TYPE_OPTIONS.filter((o) => o.value !== "CI") : TRIP_TYPE_OPTIONS}
          value={tripTypeFilter ?? ["all"]}
          onChange={(v) => onTripTypeFilterChange(Array.isArray(v) ? v : [v])}
          onRemove={() => removeFilter("trip_type")}
          multiSelect
          {...commonProps}
        />
      );
    }

    if (key === "order_type" && onOrderTypeFilterChange) {
      return (
        <ChipFilter
          key="order_type"
          label="Order Type"
          options={ORDER_TYPE_OPTIONS}
          value={orderTypeFilter ?? "all"}
          onChange={onOrderTypeFilterChange}
          onRemove={() => removeFilter("order_type")}
          {...commonProps}
        />
      );
    }

    return null;
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <DateFilterPopover dateFilter={dateFilter} onDateFilterChange={onDateFilterChange} />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Subscribed orders"
            aria-pressed={!!subscribedOnly}
            onClick={() => onSubscribedOnlyChange?.(!subscribedOnly)}
            className={cn(
              "h-9 w-9 shrink-0",
              subscribedOnly && "bg-amber-400/10 border-amber-400/60 text-amber-600 hover:bg-amber-400/20 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
            )}
          >
            <Star className={cn("h-4 w-4", subscribedOnly && "fill-amber-400 text-amber-400")} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Subscribed orders</TooltipContent>
      </Tooltip>

      {hasSearch && onClearSearch && (
        <SearchChip query={searchQuery!} onClear={onClearSearch} />
      )}

      {activeFilters.map((key) => renderChip(key))}

      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={hasAnyChipFilter}
      />

      {hasAnyChipFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={resetAllFilters}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}
